import { boardSchema } from "shared/datamodel/schemas/board";
import useBoard from "frontend/hooks/use-board";
import React, { createContext, useContext, useEffect, useReducer, useMemo } from "react";
import { randomUserDecoration } from "shared/datamodel/client-state";
import { User } from "shared/datamodel/schemas/user";
import { z } from "zod";
import LoadedState from "./loadedState";
import useStateValue from "./value";
import { BoardUser, boardUserSchema } from "shared/datamodel/schemas/board-user";
import { KonvaStage } from "frontend/konva-stage-types";

const boardConfig = z.object({
  isViewOnly: z.boolean(),
});
export type BoardConfig = z.infer<typeof boardConfig>;
export const defaultBoardConfig: BoardConfig = { isViewOnly: false };

const boardStateSchema = z.object({
  documentId: z.string(),
  isReady: z.boolean().default(false),
  user: boardUserSchema.nullish(),
  isAnonymous: z.boolean().nullish(),
  loadingUser: z.boolean().default(true),
  board: boardSchema.nullish(),
  failedLoadingBoard: z.boolean().default(false),
  isDeletedBoard: z.boolean().default(false),
  isPasswordProtected: z.boolean().default(false),
  protectedBoardReady: z.boolean().default(false),
  noAccess: z.boolean().default(false),
  isStageReady: z.boolean().default(false),
  stageRef: z.any().optional(),
  config: boardConfig,
  expiresInHours: z.number().optional(),
  showsSignup: z.boolean().default(false),
  templateElementsToCreate: z.record(z.string(), z.any()).nullish(),
  pusherChannel: z.any().optional(),
  isBoardLoaded: z.boolean().nullish().default(null),
  boardNotFound: z.boolean().default(false),
});

type BoardStateBase = z.infer<typeof boardStateSchema>;

// Properly type the BoardState with KonvaStage
export type BoardState = Omit<BoardStateBase, "stageRef"> & {
  stageRef?: KonvaStage;
};

function initialState(documentId: string): BoardState {
  return boardStateSchema.parse({
    documentId,
    config: { isViewOnly: false },
  });
}

type BoardDispatch = { type: string; payload: any | null };

function reducer(state: BoardState, action: BoardDispatch): BoardState {
  switch (action.type) {
    case "board_loaded": {
      return {
        ...state,
        isReady: true,
        board: action.payload,
        isBoardLoaded: true,
      };
    }
    case "failed_loading_board": {
      return {
        ...state,
        isReady: true,
        failedLoadingBoard: true,
        isBoardLoaded: false,
      };
    }
    case "deleted_board": {
      return {
        ...state,
        isReady: true,
        isDeletedBoard: true,
        isBoardLoaded: false,
      };
    }
    case "password_protected_board": {
      return {
        ...state,
        isReady: true,
        isPasswordProtected: true,
        isBoardLoaded: false,
      };
    }
    case "protected_board_ready": {
      return {
        ...state,
        isReady: true,
        protectedBoardReady: true,
      };
    }
    case "logged_in_user": {
      const user = action.payload as User;
      if (state.user?.isAnonymous === true) {
        // if current user is anonymous, ignore logged in user
        return state;
      }
      const { color, textColor } = randomUserDecoration();
      const boardUser: BoardUser = boardUserSchema.parse({
        ...user,
        isAnonymous: false,
        photoURL: user.thumbnail,
        color,
        textColor,
      });
      return {
        ...state,
        user: boardUser,
        loadingUser: false,
      };
    }
    case "failed_loading_user": {
      return {
        ...state,
        loadingUser: false,
      };
    }
    case "clear_user": {
      return {
        ...state,
        user: null,
      };
    }
    case "anonymous_user": {
      const user = action.payload;
      const boardUser: BoardUser = {
        isAnonymous: true,
        ...user,
      };
      return {
        ...state,
        user: boardUser,
        loadingUser: false,
      };
    }
    case "set_stage_ready": {
      return {
        ...state,
        isStageReady: true,
      };
    }
    case "set_stage_ref": {
      return {
        ...state,
        stageRef: action.payload,
      };
    }
    case "show_signup": {
      return {
        ...state,
        showsSignup: action.payload,
      };
    }
    case "set_anonymous_session": {
      return {
        ...state,
        isAnonymous: action.payload,
      };
    }
    case "set_template_elements": {
      return {
        ...state,
        templateElementsToCreate: action.payload,
      };
    }
    case "set_pusher_channel": {
      return {
        ...state,
        pusherChannel: action.payload,
      };
    }
    case "set_board": {
      return {
        ...state,
        board: action.payload,
      };
    }
    case "set_no_access": {
      return {
        ...state,
        noAccess: action.payload,
      };
    }
    case "board_not_found": {
      return {
        ...state,
        boardNotFound: action.payload,
      };
    }
    default: {
      return state;
    }
  }
}

type IBoardContext = [BoardState, React.Dispatch<BoardDispatch>];

export const BoardContext = createContext({} as IBoardContext);

declare global {
  interface Window {
    Board: any;
  }
}

export function BoardProvider({
  documentId,
  password,
  children,
}: {
  documentId: string;
  password?: string;
  children: any;
}) {
  const [{ user, userState, account }] = useStateValue();
  const [state, dispatch] = useReducer(reducer, initialState(documentId));
  const value = useMemo(() => [state, dispatch] as IBoardContext, [state, dispatch]);

  const {
    board,
    failedLoading,
    isDeletedBoard,
    isPasswordProtected,
    protectedBoardReady,
    templateElements,
    noAccess,
    boardNotFound,
  } = useBoard({
    documentId,
    password,
  });

  useEffect(() => {
    window.Board = {};
  }, []);

  useEffect(() => {
    window.Board = { ...window.Board, board, account };
    if (board) {
      dispatch({ type: "board_loaded", payload: board });
      if (account) {
        dispatch({ type: "set_anonymous_session", payload: board.accountId !== account.id });
      } else if (userState === LoadedState.failed) {
        dispatch({ type: "set_anonymous_session", payload: true });
      }
    }
    if (failedLoading) {
      dispatch({ type: "failed_loading_board", payload: null });
    }
    if (isDeletedBoard) {
      dispatch({ type: "deleted_board", payload: null });
    }
    if (isPasswordProtected) {
      dispatch({ type: "password_protected_board", payload: null });
    }
    if (protectedBoardReady) {
      dispatch({ type: "protected_board_ready", payload: null });
    }
    if (noAccess) {
      dispatch({ type: "set_no_access", payload: true });
    }
    if (boardNotFound) {
      dispatch({ type: "board_not_found", payload: true });
    }
  }, [
    board,
    account,
    failedLoading,
    isDeletedBoard,
    isPasswordProtected,
    protectedBoardReady,
    noAccess,
    boardNotFound,
  ]);

  useEffect(() => {
    if (templateElements) {
      dispatch({ type: "set_template_elements", payload: templateElements });
    }
  }, [templateElements]);

  useEffect(() => {
    if (userState === LoadedState.failed) {
      dispatch({ type: "failed_loading_user", payload: null });
    } else if (user) {
      dispatch({ type: "logged_in_user", payload: user });
    }
  }, [user, userState]);

  return <BoardContext.Provider value={value}>{children}</BoardContext.Provider>;
}

export function useBoardValue() {
  return useContext(BoardContext);
}
